import { SECOND_MS } from '@calm/web-common/dist/time';
import { Close } from '@mui/icons-material';
import commonMessages from '@rhg/lang/messages/common';
import { Alert, AlertColor, IconButton, Snackbar } from '@rhg/libs/material';
import { createContext, ReactNode, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

export const SNACKBAR_AUTO_HIDE_DURATION = 10 * SECOND_MS;
const styleInvisible = {
    // Keeps this visible to screenreaders
    opacity: 0,
};

export interface SnackbarState {
    showSnackbar: (options: SnackbarOptions) => void;
    hideSnackbar: () => void;
}
export const SnackbarContext = createContext<SnackbarState>(null!);

export interface SnackbarOptions {
    severity?: AlertColor;
    message: string;
    srOnly?: boolean;
}

interface Props {
    children: ReactNode;
}
export const SnackbarProvider = ({ children }: Props) => {
    const [options, setOptions] = useState<SnackbarOptions | undefined>(
        undefined
    );
    const [isVisible, setIsVisible] = useState(false);

    const showSnackbar = useCallback((options: SnackbarOptions) => {
        setOptions({ severity: 'info', ...options });
        setIsVisible(true);
    }, []);

    const hideSnackbar = useCallback(() => {
        setIsVisible(false);
    }, []);

    const { formatMessage } = useIntl();

    return (
        <>
            <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
                {children}
            </SnackbarContext.Provider>
            <Snackbar
                sx={options?.srOnly ? styleInvisible : {}}
                open={isVisible}
                key={options?.message}
                onClose={hideSnackbar}
                autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
                action={
                    <IconButton
                        color="inherit"
                        size="small"
                        onClick={hideSnackbar}
                        trackButton="Close"
                        aria-label={formatMessage(
                            commonMessages['common.close']
                        )}
                    >
                        <Close />
                    </IconButton>
                }
            >
                <Alert aria-live="polite" severity={options?.severity}>
                    {options?.message}
                </Alert>
            </Snackbar>
        </>
    );
};
